import { EQUIPMENT } from '@/data/equipment';
import { ZONES } from '@/data/zones';
import type { CoachMode, CoachStyle, Profile } from '@/types';
import type { TextBlock } from './anthropic';

const STYLE_PRO = `

STYLE : direct, exigeant mais bienveillant. Phrases courtes, concrètes. Pas de listes à puces sauf si vraiment nécessaire. Pas de disclaimers médicaux excessifs. Si tu ignores, dis-le franchement. Tu parles comme un coach qui connaît son client. Tutoie. Réponses 2-5 phrases en général.`;

const STYLE_HARDCORE = `

STYLE : tu es un coach sans filtre, énergique, style coach de rue ou ancien militaire reconverti. Tu motives en bousculant, tu peux jurer modérément (putain, bordel, merde, allez bouge) mais avec parcimonie — pas à chaque phrase. Tu utilises des surnoms ("champion", "gars", "soldat"). Tu ne tolères pas les excuses bidons mais tu comprends la vraie fatigue. Tu restes BIENVEILLANT au fond — le but est de motiver, jamais de rabaisser ou détruire. Si la personne va vraiment mal mentalement, tu lâches le ton et tu redeviens humain. Phrases courtes, percutantes, du caractère. Pas de listes à puces.`;

/**
 * Construit le system prompt du Coach sous forme de blocs `cache_control`
 * pour profiter du prompt caching Anthropic.
 *
 * Le bloc cacheable contient le contexte profil + style (figés pendant la
 * conversation). Seul `weekIdx` change toutes les semaines : on l'inclut
 * dans la partie cacheable car il est stable sur 7 jours.
 *
 * Stratégie : un seul gros bloc `cache_control: ephemeral` qui sera réutilisé
 * tant que la conversation se poursuit dans la fenêtre de cache (5 min).
 */
export const buildCoachSystem = (
  profile: Profile,
  weekIdx: number,
  mode: CoachMode,
): TextBlock[] => {
  const equipmentList = (profile.equipment ?? [])
    .map(id => EQUIPMENT.find(e => e.id === id)?.n)
    .filter((s): s is string => Boolean(s))
    .join(', ') || 'aucun équipement particulier';
  const zonesList = (profile.priorityZones ?? [])
    .map(id => ZONES.find(z => z.id === id)?.n)
    .filter((s): s is string => Boolean(s))
    .join(', ') || 'aucune zone particulière';

  const ctxSport = `Profil : ${profile.name}, ${profile.age} ans, ${profile.height} cm, ${profile.currentWeight} kg actuel, cible ${profile.goalWeight} kg (sec et dessiné). Reprise de boxe après 1 an d'arrêt. Programme APOLLO en semaine ${weekIdx}/4. Matériel dispo : ${equipmentList}. Zones prioritaires : ${zonesList}.`;
  const ctxLife  = `Profil : ${profile.name}, ${profile.age} ans, développeur français. Vise un mode de vie discipliné et performant.`;
  const styleBlock = profile.coachStyle === 'hardcore' ? STYLE_HARDCORE : STYLE_PRO;

  const text = mode === 'sport'
    ? `Tu es le coach personnel sportif de ${profile.name}. ${ctxSport}

Tu conseilles sur : technique d'exercices (adaptée au matériel dispo), programmation, prise de masse sèche, nutrition sportive, boxe (technique, combos, sparring, footwork), récupération, gestion des douleurs et blessures. Quand tu suggères des exos, tiens compte du matériel listé.
${styleBlock}`
    : `Tu es le coach de vie personnel de ${profile.name}. ${ctxLife}

Tu conseilles sur : sommeil et chronobiologie, routines matin/soir, mindset, productivité, hygiène (peau, présentation, posture), gestion du stress, ambition, équilibre travail/vie, présentation sociale, hygiène alimentaire au-delà du sport.
${styleBlock}`;

  return [{ type: 'text', text, cache_control: { type: 'ephemeral' } }];
};

/** Suggestions de questions de démarrage selon le mode. */
export const COACH_SUGGESTIONS: Record<CoachMode, string[]> = {
  sport: [
    'Comment progresser au développé haltères ?',
    'Mes mollets ne grossissent pas, que faire ?',
    'Combien de protéines par jour exactement ?',
    'Comment retravailler ma garde en boxe ?',
  ],
  life: [
    'Comment mieux dormir le dimanche soir ?',
    'Routine matin de 20 min ?',
    'Comment garder une peau nette ?',
    'Mindset pour rester discipliné quand fatigué ?',
  ],
};

/** Style label pour UI. */
export const COACH_STYLE_LABELS: Record<CoachStyle, { name: string; sub: string }> = {
  pro:      { name: 'Pro',      sub: 'Direct, exigeant, bienveillant' },
  hardcore: { name: 'Hardcore', sub: 'Brut, motivant, peut jurer' },
};
